import { storageGet, storageRemove, storageSet } from '@faceup/utils'
import type { InferType, Schema } from '@faceup/validation'
import merge from 'deepmerge'

// biome-ignore lint/suspicious/noExplicitAny: Yup schema generic requires any type parameter
export type AnySchema = Schema<any>

type StorageArea = 'local' | 'session'

type StorageFormat = 'json' | 'string'

export type StorageItemDefinition<S extends AnySchema> = {
  key: string
  schema: S
  /** Returned while nothing is stored, or when the stored value cannot be repaired to match `schema`. */
  defaultValue: InferType<S>
  /** `local` (default) outlives the browser session, `session` lives as long as the tab. */
  area?: StorageArea
  /**
   * `json` (default) round-trips any value. `string` stores a string as is, for a key that code
   * outside this module also reads raw, and removes the key when set to `null`.
   */
  format?: InferType<S> extends string | null ? StorageFormat : 'json'
}

type SetStateAction<T> = T | ((current: T) => T)

export type StorageItem<T> = {
  key: string
  get: () => T
  /** Returns whether the value reached the browser storage rather than only this page's memory. */
  set: (value: SetStateAction<T>) => boolean
  remove: () => void
  subscribe: (listener: () => void) => () => void
}

const webStorageNames = {
  local: 'localStorage',
  session: 'sessionStorage',
} as const

// Listeners are keyed by the storage slot rather than held per item, so two items defined for the
// same key (a module re-evaluated by HMR, say) still see each other's writes.
const listeners = new Map<string, Set<() => void>>()

const toSlot = (area: StorageArea, key: string) => `${area}:${key}`

const notify = (slot: string) => {
  listeners.get(slot)?.forEach(listener => listener())
}

const notifyAll = () => {
  listeners.forEach(slotListeners => slotListeners.forEach(listener => listener()))
}

const subscribeToSlot = (slot: string, listener: () => void) => {
  listenToOtherDocuments()
  const slotListeners = listeners.get(slot) ?? new Set()
  listeners.set(slot, slotListeners.add(listener))

  return () => {
    slotListeners.delete(listener)
  }
}

let isListeningToOtherDocuments = false

const listenToOtherDocuments = () => {
  if (isListeningToOtherDocuments || typeof window === 'undefined') {
    return
  }
  isListeningToOtherDocuments = true

  // Fired for writes made by other tabs only, never by this one
  window.addEventListener('storage', ({ key }) => {
    // A null key means the other tab cleared the whole storage
    if (key === null) {
      notifyAll()
      return
    }
    // Both areas are notified because reading `storageArea` back can throw where storage is
    // blocked. A listener whose value did not change re-reads the same snapshot and React skips
    // the render.
    notify(toSlot('local', key))
    notify(toSlot('session', key))
  })
}

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const isContainer = (value: unknown) => typeof value === 'object' && value !== null

/**
 * The one way to keep a value in `localStorage` or `sessionStorage`. Define every key once, at module
 * scope, and read it with `useStorageItem` in React or with `get` everywhere else.
 *
 * Reads are validated against `schema`. A stored object that no longer matches it is merged onto
 * `defaultValue` first, so a schema that gained a field keeps what the user had saved, and anything
 * that still does not match falls back to `defaultValue`. Access goes through the
 * `storageGet`/`storageSet` primitives of `@faceup/utils`, so blocked or full storage degrades to an
 * in-memory copy for the rest of the page's life instead of throwing.
 */
export const defineStorageItem = <S extends AnySchema>({
  key,
  schema,
  defaultValue,
  area = 'local',
  format = 'json',
}: StorageItemDefinition<S>) => {
  type Value = InferType<S>

  // An object or array is checked loosely, because JSON stores the Dates in it as ISO strings and
  // only casting lets `yup.date()` accept them. A lone value has to have its type already: cast,
  // the text "false" would pass for a boolean and then read as true.
  const isValid = (value: unknown): value is Value => {
    try {
      return schema.isValidSync(value, { strict: !isContainer(value) })
    } catch {
      // yup throws rather than fails on own keys that shadow `Object.prototype` (`constructor`)
      return false
    }
  }

  if (!isValid(defaultValue)) {
    throw new Error(`Default value of the "${key}" storage item does not match its schema`)
  }

  const webStorage = webStorageNames[area]
  const slot = toSlot(area, key)

  const decode = (raw: string): unknown => {
    if (format === 'string') {
      return raw
    }
    try {
      return JSON.parse(raw)
    } catch {
      // Written raw before the key moved to JSON, the schema decides whether it is still usable
      return raw
    }
  }

  // The value is returned as stored, uncast, so a Date written into an object comes back as the
  // ISO string
  const toValidValue = (value: unknown): Value => {
    if (isValid(value)) {
      return value
    }
    // Merging anything but two objects would turn a corrupt string into an object of its characters
    if (!isPlainObject(defaultValue) || !isPlainObject(value)) {
      return defaultValue
    }
    try {
      const repaired = merge(defaultValue, value, { arrayMerge: (_default, stored) => stored })
      return isValid(repaired) ? repaired : defaultValue
    } catch {
      // A value nested deeply enough overflows the recursive merge
      return defaultValue
    }
  }

  // `useSyncExternalStore` compares snapshots by reference, so an unchanged raw string has to yield
  // the very same object instead of a freshly parsed copy.
  let lastRead: { raw: string; value: Value } | undefined

  const get = (): Value => {
    const raw = storageGet(webStorage, key)
    if (raw === null) {
      return defaultValue
    }
    if (lastRead?.raw !== raw) {
      lastRead = { raw, value: toValidValue(decode(raw)) }
    }
    return lastRead.value
  }

  const remove = () => {
    storageRemove(webStorage, key)
    notify(slot)
  }

  const set = (action: SetStateAction<Value>) => {
    const value = action instanceof Function ? action(get()) : action
    // Stored as the text "undefined", or "null" in the string format, either would read back as a
    // valid string
    if (value === undefined || (format === 'string' && value === null)) {
      remove()
      return true
    }
    const isPersisted = storageSet(
      webStorage,
      key,
      format === 'string' ? String(value) : JSON.stringify(value)
    )
    notify(slot)
    return isPersisted
  }

  return {
    key,
    get,
    set,
    remove,
    subscribe: (listener: () => void) => subscribeToSlot(slot, listener),
  }
}

/**
 * For a key that embeds a parameter, such as the id of the logged-in user. Each parameter gets one
 * item, created on first use and reused afterwards, which `useStorageItem` needs to keep its
 * subscription and snapshot stable across renders.
 */
export const defineStorageItemFamily = <P extends string, S extends AnySchema>({
  key,
  ...definition
}: Omit<StorageItemDefinition<S>, 'key'> & { key: (parameter: P) => string }) => {
  const items = new Map<P, StorageItem<InferType<S>>>()

  return (parameter: P) => {
    const existing = items.get(parameter)
    if (existing) {
      return existing
    }
    const item = defineStorageItem({ ...definition, key: key(parameter) })
    items.set(parameter, item)
    return item
  }
}
